import React, { useEffect, useState } from "react"
import { injectIntl } from "react-intl"
import { merge } from "lodash"
import LoadingSpinner from "../../common/spinner/loading.component"
import { isError } from "../../state/helpers/Error"
import IntlProps from "../../state/helpers/IntlProps"
import SavedFilter from "../../state/listing/SavedFilter"
import SavedFilterService from "../../state/listing/SavedFilterService"
import { ShortcutDto } from "../../state/shortcut/Shortcut"
import ColorDropdownButton from "./ColorDropdownButton"
import FormRow from "./FormRow"
import { filterMessages } from "../messages/messages"
import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import Select from "react-select"
import { objectMapping, filterObjectsRequiredFeatures } from "./helpers.jsx"
import SelectOptionWrapper from "../../common/rich-text/feature-protection-wrappers/selectOptionWrapper.component"

interface Props {
    value: ShortcutDto,
    onChange: (value: React.SetStateAction<ShortcutDto>) => void,
}


const ShortcutFilterSettings: React.FC<Props & IntlProps> = ({value, onChange, intl}) => {

    const [errorMsg, setErrorMsg] = useState<string>("")
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([])

    useEffect(() => {
        getSavedFilters()
    }, [])

    const getSavedFilters = () => {
        SavedFilterService.getAllDashboardSavedFilters().then((r) => {
            if (!isError(r)) {
                setSavedFilters(r)
                setIsLoading(false)
            } else {
                setErrorMsg(r.message)
            }
        })
    }

    const onLabelChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        onChange(merge(value, {
            attributes: {
                name: event.target.value,
            }
        }))
    }

    const onFilterChange = (elem) => {
        onChange(merge(value, {
            attributes: {
                savedFilter: {
                    id: elem.value,
                }
            }
        }))
    }

    const onColorChange = (color: string) => {
        onChange(merge(value, {
            attributes: {
                color,
            }
        }))
    }

    const getFilterSelectOptions = () => {
        return savedFilters.map(savedFilter => {
            const [object] = objectMapping[savedFilter.getListingType()]
            let protectionRuleReducer = true
            if (filterObjectsRequiredFeatures[object]) {
                protectionRuleReducer = filterObjectsRequiredFeatures[object].reduce((final, feature) => {
                    return final && AppContext.accountFeatureIsEnabled(feature)
                }, true)
            }
            return {
                label: <SelectOptionWrapper
                    premium={false}
                    protectionRule={protectionRuleReducer}
                    redirectToAccount={true}
                >
                    <span>{savedFilter.getName()}</span>
                </SelectOptionWrapper>,
                value: savedFilter.getId(),
                disabled: !protectionRuleReducer
            }
        })
    }

    if (isLoading) {
        return (
            <LoadingSpinner height={15} width={3} className="px-5"/>
        )
    }

    return (
        <div className="pt-4">
            {/* Saved filter row */}
            <FormRow
                label={intl.formatMessage(filterMessages.shortcutFilterSavedFilterLabel)}
                errorMessage={intl.formatMessage(filterMessages.shortcutFilterSavedFilterError)}
            >
                <Select
                    placeholder={intl.formatMessage(filterMessages.shortcutFilterDefaultSavedFilter)}
                    options={getFilterSelectOptions()}
                    clearable={false}
                    searchable={false}
                    multi={false}

                    value={value.attributes.savedFilter?.id || ""}
                    onChange={onFilterChange}
                />
            </FormRow>
            <div className="text-danger">{errorMsg}</div>

            {/* Label row */}
            <FormRow
                label={intl.formatMessage(filterMessages.shortcutFilterLabel)}
                errorMessage={intl.formatMessage(filterMessages.shortcutFilterInputError)}
            >
                <input
                    maxLength={20}
                    value={value.attributes.name || ""}
                    required
                    onChange={onLabelChange}
                    type="text"
                    className="form-control"
                />
            </FormRow>

            {/* Color row */}
            <FormRow label={intl.formatMessage(filterMessages.shortcutFilterColorLabel)} sm="4">
                <ColorDropdownButton value={value.attributes.color} onChange={onColorChange}/>
            </FormRow>
        </div>
    )
}

export default injectIntl(ShortcutFilterSettings)




// WEBPACK FOOTER //
// ./src/shortcuts/components/ShortcutFilterSettings.tsx